<template>
  <div class="login-layout">
    <!-- Lado izquierdo - Información de la marca/tienda -->
    <div class="login-brand-section" :style="backgroundStyle">
      <!-- Información superior de la empresa -->
      <div class="company-header">
        <div class="company-header-content">
          <div class="logo-wrapper">
            <img src="@/assets/logo.png" alt="Logo" class="company-logo" />
          </div>
          <div class="company-info">
            <h1 class="company-name">
              {{ storeData.store_name || storeData.PartyName || "AI-POS" }}
            </h1>
            <p class="company-type">
              {{ storeData.store_category || "Sistema de Punto de Venta Inteligente" }}
              <v-chip
                v-if="hasStoreData"
                :color="getDueDateColor"
                text-color="white"
                small
                class="ml-6 status-chip"
              >
                <v-icon left x-small>mdi-shield-check-outline</v-icon>
                {{ getDueDateText }}
              </v-chip>
            </p>
          </div>
        </div>
      </div>

      <!-- Área central transparente - Solo espacio vacío para ver la imagen -->
      <div class="center-area">
        <!-- Área completamente transparente para mostrar la imagen de fondo -->
      </div>

      <!-- Información de contacto inferior -->
      <div class="contact-footer" v-if="hasStoreData || credentials.store">
        <div class="contact-footer-content">
          <div class="contact-item">
            <v-icon size="18" color="white">mdi-map-marker</v-icon>
            <span>{{
              storeData.store_address || storeData.store_city || "Dirección de la tienda"
            }}</span>
          </div>
          <div class="contact-item">
            <v-icon size="18" color="white">mdi-phone</v-icon>
            <span>{{ storeData.store_phone || "Teléfono de contacto" }}</span>
          </div>
          <div class="contact-item">
            <v-icon size="18" color="white">mdi-email</v-icon>
            <span>{{
              storeData.store_email || credentials.email || "info@tienda.com"
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Lado derecho - Formulario de login -->
    <div class="login-form-section">
      <div class="form-container">
        <!-- Header del formulario -->
        <div class="form-header">
          <h2 class="form-title">{{ isReturningUser ? 'Bienvenido de nuevo' : 'Iniciar Sesión' }}</h2>
          <p class="form-description" v-if="isReturningUser && storeData.store_name">
            {{ storeData.store_name }}
            <br />
            <v-btn text x-small color="grey darken-1" class="mt-1 not-you-link" @click="clearSavedSession">
              ¿No eres tú?
            </v-btn>
          </p>
          <p class="form-description" v-else>Accede a tu punto de venta inteligente</p>
        </div>

        <!-- Formulario -->
        <v-form
          ref="loginForm"
          v-model="isFormValid"
          @submit.prevent="handleLogin"
          class="login-form"
        >
          <!-- Campo Código de Tienda -->
          <div class="input-section">
            <label class="field-label">Código de Tienda</label>
            <v-text-field
              ref="storeField"
              v-model="credentials.store"
              :rules="storeValidationRules"
              placeholder="123456000"
              outlined
              dense
              hide-details="auto"
              type="tel"
              inputmode="numeric"
              autocomplete="organization"
              @blur="validateStoreCode"
              @keyup.enter="$refs.emailField && $refs.emailField.focus()"
              :loading="isValidatingStore"
              class="custom-input"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey darken-1" size="20">mdi-store-outline</v-icon>
              </template>

              <template v-slot:append v-if="isStoreValidated">
                <v-icon color="success" size="20">mdi-check-circle</v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Campo Email -->
          <div class="input-section">
            <label class="field-label">Correo Electrónico</label>
            <v-text-field
              ref="emailField"
              v-model="credentials.email"
              :rules="emailValidationRules"
              placeholder="usuario@mitienda.com"
              outlined
              dense
              hide-details="auto"
              type="email"
              autocomplete="email"
              @keyup.enter="$refs.passwordField && $refs.passwordField.focus()"
              class="custom-input"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey darken-1" size="20">mdi-email-outline</v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Campo Contraseña -->
          <div class="input-section">
            <div class="field-header">
              <label class="field-label">Contraseña</label>
              <v-btn
                text
                x-small
                color="grey darken-1"
                @click="showForgotPassword"
                class="forgot-link"
              >
                ¿Olvidaste tu contraseña?
              </v-btn>
            </div>

            <v-text-field
              ref="passwordField"
              v-model="credentials.password"
              :rules="passwordValidationRules"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Tu contraseña segura"
              outlined
              dense
              hide-details="auto"
              autocomplete="current-password"
              @keyup.enter="handleLogin"
              class="custom-input"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey darken-1" size="20">mdi-lock-outline</v-icon>
              </template>

              <template v-slot:append>
                <v-btn icon small @click="showPassword = !showPassword" tabindex="-1">
                  <v-icon size="20" color="grey darken-1">
                    {{ showPassword ? "mdi-eye-off" : "mdi-eye" }}
                  </v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </div>

          <!-- Recordar sesión -->
          <div class="form-options">
            <v-checkbox
              v-model="rememberSession"
              color="grey darken-2"
              hide-details
              dense
              class="remember-checkbox"
            >
              <template v-slot:label>
                <span class="remember-label">Recordar mi sesión</span>
              </template>
            </v-checkbox>
          </div>

          <!-- Botón de login -->
          <v-btn
            color="primary"
            block
            large
            depressed
            :loading="isLoggingIn"
            :disabled="!canSubmit"
            type="submit"
            class="login-button"
          >
            <v-icon left size="20" color="white">mdi-login</v-icon>
            <span style="color: white; font-weight: 600">Acceder al Sistema</span>
          </v-btn>
        </v-form>

        <!-- Footer del formulario -->
        <div class="form-footer">
          <div class="security-badge">
            <v-icon color="success" size="16">mdi-shield-check</v-icon>
            <span>Conexión Segura</span>
          </div>

          <div class="version-info">{{ brandConfig.title }} v2.0 • {{ currentYear }}</div>
        </div>
      </div>
    </div>

    <!-- Notificaciones -->
    <v-snackbar
      v-model="notification.show"
      :color="notification.type"
      :top="!$vuetify.breakpoint.smAndDown"
      :bottom="$vuetify.breakpoint.smAndDown"
      shaped
      :timeout="notification.timeout"
    >
      <div class="notification-content">
        <v-icon :color="getNotificationIconColor" class="mr-2">
          {{ getNotificationIcon }}
        </v-icon>
        <span>{{ notification.message }}</span>
      </div>

      <template v-slot:action="{ attrs }">
        <v-btn
          text
          small
          v-bind="attrs"
          @click="notification.show = false"
          :color="getNotificationIconColor"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Overlay de carga -->
    <v-overlay v-model="showLoadingOverlay" opacity="0.6" z-index="9999">
      <div class="loading-content">
        <v-progress-circular
          indeterminate
          color="grey darken-3"
          size="56"
          width="4"
        ></v-progress-circular>
        <div class="loading-text">{{ loadingMessage }}</div>
      </div>
    </v-overlay>

    <!-- Gate de licencia (Tollkeep): diálogo de cobro si la empresa no está al día -->
    <license-gate ref="gate"></license-gate>
  </div>
</template>

<script>
import { webserver } from "../services/webserver.js";
import createProfile from "../utils/createProfile.js";
import LicenseGate from "./LicenseGate.vue";

const DEBUG = process.env.NODE_ENV !== "production";
const log = (...args) => { if (DEBUG) console.log(...args); };

export default {
  name: "LoginForm",
  components: { LicenseGate },

  props: {
    nextUrl: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isFormValid: false,
      isLoggingIn: false,
      isValidatingStore: false,
      showLoadingOverlay: false,
      loadingMessage: "Iniciando sesión...",
      showPassword: false,
      rememberSession: false,
      isStoreValidated: false,
      daysDifference: 100,

      notification: {
        show: false,
        message: "",
        type: "error",
        timeout: 5000,
      },

      // Reglas de validación
      storeValidationRules: [
        (v) => !!v || "El código de tienda es obligatorio",
        (v) => (v && v.toString().length >= 9) || "Mínimo 9 dígitos requeridos",
        (v) => /^\d+$/.test(v) || "Solo números permitidos",
      ],

      emailValidationRules: [
        (v) => !!v || "El correo electrónico es obligatorio",
        (v) => /.+@.+\..+/.test(v) || "Formato de correo inválido",
      ],

      passwordValidationRules: [
        (v) => !!v || "La contraseña es obligatoria",
        (v) => (v && v.length >= 3) || "Mínimo 3 caracteres",
      ],

      credentials: {
        store: null,
        email: null,
        password: null,
      },

      profile: createProfile(),

      // Configuración de marca
      brandConfig: {
        title: "AI-POS",
        subtitle: "Sistema de Punto de Venta Inteligente",
      },

      // Configuraciones por dominio
      domainConfigs: {
        "restaurant.ai-pos.co": {
          title: "AI-POS Restaurant",
          subtitle: "Punto de venta para Restaurantes",
        },
        "retail.ai-pos.co": {
          title: "AI-POS Retail",
          subtitle: "Sistema para Tiendas",
        },
        "tpv.ai-pos.co": {
          title: "TPV Pro",
          subtitle: "Terminal Punto de Venta",
        },
      },

      storeData: {
        store_name: "",
        store_category: "",
        store_city: "",
        PartyName: "",
        dueDate: "",
        status: "",
        store_address: "",
        store_phone: "",
        store_email: "",
        store_owner: "",
      },

      customBackgroundUrl: null,
      isSearchingStoreBackground: false,
      storeBackgroundFound: false,
      defaultBackgroundUrl:
        "https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",

      // Imágenes por defecto para cada categoría
      categoryBackgrounds: {
        // Restaurantes y comida
        restaurante:
          "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=2000&q=80",
        restaurant:
          "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=2000&q=80",
        café:
          "https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&w=2000&q=80",
        cafe:
          "https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&w=2000&q=80",
        coffee:
          "https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&w=2000&q=80",
        bar:
          "https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=2000&q=80",
        pizzería:
          "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=2000&q=80",
        panadería:
          "https://images.unsplash.com/photo-1555507036-ab1f4038808a?auto=format&fit=crop&w=2000&q=80",

        // Retail y tiendas
        retail:
          "https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=2000&q=80",
        tienda:
          "https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=2000&q=80",
        moda:
          "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=2000&q=80",
        ropa:
          "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=2000&q=80",
        boutique:
          "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=2000&q=80",
        zapatería:
          "https://images.unsplash.com/photo-1460353581641-37baddab0fa2?auto=format&fit=crop&w=2000&q=80",

        // Supermercados y alimentación
        supermercado:
          "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?auto=format&fit=crop&w=2000&q=80",
        mercado:
          "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=2000&q=80",
        frutería:
          "https://images.unsplash.com/photo-1610832958506-aa56368176cf?auto=format&fit=crop&w=2000&q=80",
        carnicería:
          "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=2000&q=80",
        carniceria:
          "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=2000&q=80",
        butcher:
          "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=2000&q=80",

        // Tecnología
        tecnología:
          "https://images.unsplash.com/photo-1491933382434-500287f9b54b?auto=format&fit=crop&w=2000&q=80",
        electrónica:
          "https://images.unsplash.com/photo-1491933382434-500287f9b54b?auto=format&fit=crop&w=2000&q=80",
        informática:
          "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=2000&q=80",

        // Salud y belleza
        farmacia:
          "https://images.unsplash.com/photo-1576602976047-174e57a47881?auto=format&fit=crop&w=2000&q=80",
        salon:
          "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=2000&q=80",
        peluquería:
          "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=2000&q=80",
        spa:
          "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=2000&q=80",

        // Servicios
        servicios:
          "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80",
        hotel:
          "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=2000&q=80",
        gimnasio:
          "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=2000&q=80",
        gym:
          "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=2000&q=80",
      },

      categoryColors: {
        Restaurante: "#757575",
        Restaurant: "#757575",
        Café: "#616161",
        Retail: "#424242",
        Tienda: "#424242",
        Moda: "#757575",
        Supermercado: "#616161",
        Tecnología: "#424242",
        Farmacia: "#757575",
        Servicios: "#616161",
        default: "#616161",
      },
    };
  },

  computed: {
    backgroundStyle() {
      const bgUrl = this.customBackgroundUrl || this.defaultBackgroundUrl;
      return {
        backgroundImage: `url('${bgUrl}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    },

    currentYear() {
      return new Date().getFullYear();
    },

    hasStoreData() {
      return this.storeData && (this.storeData.store_name || this.storeData.PartyName);
    },

    getCategoryColor() {
      return (
        this.categoryColors[this.storeData.store_category] || this.categoryColors.default
      );
    },

    getDueDateColor() {
      if (this.daysDifference <= 0) return "error";
      if (this.daysDifference <= 5) return "warning";
      return "success";
    },

    getDueDateText() {
      if (this.daysDifference <= 0) return "Cuenta Vencida";
      if (this.daysDifference <= 30) return `${this.daysDifference} días restantes`;
      return "Cuenta Activa";
    },

    isReturningUser() {
      return !!(this.rememberSession && this.credentials.store && this.credentials.email);
    },

    canSubmit() {
      return (
        this.isFormValid &&
        this.credentials.store &&
        this.credentials.email &&
        this.credentials.password
      );
    },

    getNotificationIcon() {
      switch (this.notification.type) {
        case "success":
          return "mdi-check-circle";
        case "warning":
          return "mdi-alert";
        case "info":
          return "mdi-information";
        default:
          return "mdi-alert-circle";
      }
    },

    getNotificationIconColor() {
      return this.notification.type === "error" ? "white" : this.notification.type;
    },
  },

  watch: {
    "credentials.store": function (newVal) {
      if (newVal && newVal.length >= 9) {
        log("Store credential changed:", newVal);
        this.checkCustomBackground();
      }
    },
    "storeData.store_owner": function (newVal) {
      if (newVal) {
        log("Store owner changed:", newVal);
        this.checkCustomBackground();
      }
    },
    "storeData.store_category": function (newVal) {
      if (newVal) {
        log("Store category changed:", newVal);
        // Solo cargar por categoría si no hay imagen de tienda (ni buscándose ni encontrada)
        if (!this.customBackgroundUrl && !this.isSearchingStoreBackground && !this.storeBackgroundFound) {
          this.loadCategoryBackground();
        }
      }
    },
  },

  created() {
    this.loadSavedCredentials();
    this.loadSavedStoreData();
    this.detectDomainAndSetBranding();
  },

  mounted() {
    this.$store.dispatch("setNavStatus", false);
    this.$store.dispatch("set_menu", false);
    this.calculateDueDateDifference();
    // Verificar si hay imagen personalizada al montar el componente
    this.checkCustomBackground();

    // Auto-focus en el primer campo vacío
    this.$nextTick(() => {
      if (!this.credentials.store) {
        this.$refs.storeField && this.$refs.storeField.focus();
      } else if (!this.credentials.email) {
        this.$refs.emailField && this.$refs.emailField.focus();
      } else {
        this.$refs.passwordField && this.$refs.passwordField.focus();
      }
    });
  },

  methods: {
    checkCustomBackground() {
      // Resetear estado
      this.customBackgroundUrl = null;
      this.isSearchingStoreBackground = false;
      this.storeBackgroundFound = false;

      if (this.storeData.store_owner || this.credentials.store) {
        const storeOwner = this.storeData.store_owner || this.credentials.store;

        // Probar múltiples ubicaciones posibles para la imagen específica de la tienda
        const possiblePaths = [
          `/assets/${storeOwner}/bg.jpg`,
          `/img/${storeOwner}/bg.jpg`,
          `/${storeOwner}/bg.jpg`,
          `/assets/${storeOwner}.jpg`,
          `/img/${storeOwner}.jpg`,
        ];

        log("Verificando imagen personalizada para tienda:", storeOwner);
        this.isSearchingStoreBackground = true;

        // Función para probar cada ruta
        const tryLoadImage = (paths, index = 0) => {
          if (index >= paths.length) {
            log("❌ No se encontró imagen personalizada para la tienda");
            this.isSearchingStoreBackground = false;
            // Solo cargar categoría si no se encontró imagen de tienda
            this.loadCategoryBackground();
            return;
          }

          const currentPath = paths[index];
          log(`Intentando cargar: ${currentPath}`);

          const img = new Image();

          // Agregar timestamp para evitar caché
          const cacheBuster = `?t=${new Date().getTime()}`;

          img.onload = () => {
            log("✅ Imagen personalizada de tienda encontrada:", currentPath);
            this.isSearchingStoreBackground = false;
            this.storeBackgroundFound = true;
            this.customBackgroundUrl = currentPath + cacheBuster;
            this.$forceUpdate();
          };

          img.onerror = () => {
            log(`❌ No se pudo cargar: ${currentPath}`);
            // Intentar con la siguiente ruta
            tryLoadImage(paths, index + 1);
          };

          // Cargar con cache buster
          img.src = currentPath + cacheBuster;
        };

        // Iniciar la búsqueda
        tryLoadImage(possiblePaths);
      } else {
        log("No hay store_owner o credentials.store disponible");
        // Intentar cargar imagen por categoría si está disponible
        this.loadCategoryBackground();
      }
    },

    loadCategoryBackground() {
      // No sobreescribir si ya se encontró imagen de tienda
      if (this.storeBackgroundFound) {
        log("⏭️ Imagen de tienda ya encontrada, omitiendo categoría");
        return;
      }

      log("🔍 Iniciando loadCategoryBackground");
      log("📊 storeData completo:", this.storeData);
      log("🏷️ store_category disponible:", this.storeData.store_category);

      const category = this.storeData.store_category
        ? this.storeData.store_category.toLowerCase().trim()
        : "café"; // Usar café como default para testing

      log("🔍 Categoría a buscar:", category);

      // Primero intentar con imagen local en categorias/
      const localCategoryPaths = [
        `/categorias/${category}.jpg`,
        `/assets/categories/${category}.jpg`,
        `/img/categories/${category}.jpg`,
        `/categorias/${category}.png`,
        `/assets/categories/${category}.png`,
      ];

      log("🔍 Buscando imágenes locales para:", category);

      const tryLoadLocalImage = (paths, index = 0) => {
        if (index >= paths.length) {
          log("❌ No se encontró imagen local, usando imagen online");
          // Fallback a imagen online
          this.loadOnlineCategoryBackground(category);
          return;
        }

        const currentPath = paths[index];
        log(`🔍 Intentando imagen local: ${currentPath}`);

        const img = new Image();
        const cacheBuster = `?t=${new Date().getTime()}`;

        img.onload = () => {
          if (this.storeBackgroundFound) return;
          log("✅ Imagen local de categoría encontrada:", currentPath);
          this.customBackgroundUrl = currentPath + cacheBuster;
          this.$forceUpdate();
        };

        img.onerror = () => {
          log(`❌ No se pudo cargar imagen local: ${currentPath}`);
          // Intentar con la siguiente ruta local
          tryLoadLocalImage(paths, index + 1);
        };

        img.src = currentPath + cacheBuster;
      };

      // Iniciar búsqueda de imágenes locales
      tryLoadLocalImage(localCategoryPaths);
    },

    loadOnlineCategoryBackground(category) {
      // No sobreescribir si ya se encontró imagen de tienda
      if (this.storeBackgroundFound) return;

      log("🌐 Buscando imagen online para categoría:", category);

      // Buscar imagen online para la categoría
      const categoryImage = this.categoryBackgrounds[category];
      log("🖼️ Imagen online encontrada:", categoryImage ? "SÍ" : "NO");

      if (categoryImage) {
        log("🎨 Usando imagen online de categoría:", category);
        log("🔗 URL de imagen:", categoryImage);
        this.customBackgroundUrl = categoryImage;
      } else {
        log(
          "🖼️ Categoría exacta no encontrada online, buscando coincidencias parciales"
        );
        log("🔍 Categorías disponibles:", Object.keys(this.categoryBackgrounds));

        // Buscar palabras clave en la categoría
        const foundCategory = Object.keys(this.categoryBackgrounds).find(
          (key) => category.includes(key) || key.includes(category)
        );

        if (foundCategory) {
          log("🎨 Categoría parcial encontrada:", foundCategory);
          this.customBackgroundUrl = this.categoryBackgrounds[foundCategory];
        } else {
          log(
            "❌ No se encontró ninguna coincidencia, usando imagen por defecto"
          );
          this.customBackgroundUrl = null;
        }
      }

      log("✅ Imagen final seleccionada:", this.customBackgroundUrl);
      this.$forceUpdate();
    },

    loadSavedCredentials() {
      try {
        const savedStore = localStorage.getItem("ai_pos_store");
        const savedEmail = localStorage.getItem("ai_pos_email");

        if (savedStore && savedEmail) {
          this.credentials.store = savedStore;
          this.credentials.email = savedEmail;
          this.rememberSession = true;
          this.isStoreValidated = true;
        }
      } catch (error) {
        console.error("Error cargando credenciales:", error);
      }
    },

    loadSavedStoreData() {
      try {
        const savedStoreData = localStorage.getItem("ai_pos_store_data");
        log("Datos de tienda cargados:", savedStoreData);
        if (savedStoreData) {
          this.storeData = JSON.parse(savedStoreData);
          this.calculateDueDateDifference();
          // Verificar imagen personalizada cuando se cargan datos guardados
          this.checkCustomBackground();
        }
      } catch (error) {
        console.error("Error cargando datos de tienda:", error);
      }
    },

    calculateDueDateDifference() {
      if (this.storeData.dueDate) {
        const today = new Date();
        const expirationDate = new Date(this.storeData.dueDate);
        const diffTime = expirationDate.getTime() - today.getTime();
        this.daysDifference = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }
    },

    saveCredentials() {
      try {
        if (this.rememberSession) {
          localStorage.setItem("ai_pos_store", this.credentials.store);
          localStorage.setItem("ai_pos_email", this.credentials.email);
        } else {
          localStorage.removeItem("ai_pos_store");
          localStorage.removeItem("ai_pos_email");
        }
      } catch (error) {
        console.error("Error guardando credenciales:", error);
      }
    },

    saveStoreData(storeData) {
      try {
        if (storeData && typeof storeData === "object") {
          localStorage.setItem("ai_pos_store_data", JSON.stringify(storeData));
        }
      } catch (error) {
        console.error("Error guardando datos de tienda:", error);
      }
    },

    detectDomainAndSetBranding() {
      const currentHostname = window.location.hostname;
      if (this.domainConfigs[currentHostname]) {
        this.brandConfig = this.domainConfigs[currentHostname];
      }
    },

    async validateStoreCode() {
      if (this.credentials.store && this.credentials.store.length >= 9) {
        this.isStoreValidated = true;
        // Verificar si existe imagen personalizada para esta tienda
        this.checkCustomBackground();
      }
    },

    showNotification(message, type = "error", timeout = 5000) {
      this.notification.message = message;
      this.notification.type = type;
      this.notification.timeout = timeout;
      this.notification.show = true;
    },

    clearSavedSession() {
      localStorage.removeItem("ai_pos_store");
      localStorage.removeItem("ai_pos_email");
      localStorage.removeItem("ai_pos_store_data");
      this.credentials.store = null;
      this.credentials.email = null;
      this.credentials.password = null;
      this.rememberSession = false;
      this.isStoreValidated = false;
      this.storeData = {
        store_name: "",
        store_category: "",
        store_city: "",
        PartyName: "",
        dueDate: "",
        status: "",
        store_address: "",
        store_phone: "",
        store_email: "",
        store_owner: "",
      };
      this.customBackgroundUrl = null;
      this.$nextTick(() => {
        this.$refs.storeField && this.$refs.storeField.focus();
      });
    },

    showForgotPassword() {
      this.showNotification(
        "Contacta al administrador para recuperar tu contraseña",
        "info",
        8000
      );
    },

    handleLoginError(message, clearPassword = true) {
      this.isLoggingIn = false;
      this.showLoadingOverlay = false;
      if (clearPassword) {
        this.credentials.password = "";
      }
      this.showNotification(message);
      this.$nextTick(() => {
        this.isFormValid = false;
      });
    },

    async handleLogin(e) {
      if (e) e.preventDefault();

      if (!this.canSubmit) {
        this.showNotification("Complete todos los campos correctamente");
        return;
      }

      this.isLoggingIn = true;
      this.showLoadingOverlay = true;
      this.loadingMessage = "Validando licencia...";

      // Gate de licencia (Tollkeep): si la empresa no está al día, abre el diálogo de cobro y no continúa.
      if (!(await this.$refs.gate.validate(this.credentials.store))) {
        this.isLoggingIn = false;
        this.showLoadingOverlay = false;
        return;
      }

      this.loadingMessage = "Verificando credenciales...";

      try {
        const md5 = require("md5");
        const passwordHash = md5(this.credentials.password);

        const loginData = {
          store: this.credentials.store,
          user_email: this.credentials.email,
          user_password: passwordHash,
        };

        const self = this;

        webserver("login", loginData, (data) => {
          log("Respuesta del servidor:", data);
          try {
            if (!data || data.error) {
              const isAuthError = data?.message?.toLowerCase().includes("contraseña") ||
                data?.message?.toLowerCase().includes("password") ||
                data?.message?.toLowerCase().includes("credencial");
              self.handleLoginError(data?.message || "Credenciales incorrectas", isAuthError || !data?.message);
              return;
            }

            // Validaciones existentes...
            const validationResult = self.validateLoginResponse(data);
            if (validationResult.error) {
              self.handleLoginError(validationResult.message, false);
              return;
            }

            self.loadingMessage = "Configurando sesión...";

            // Procesar login exitoso
            self.saveCredentials();

            const sellerData = data.seller || {
              PartyName: data.store?.store_name || "Sin asignar",
              PartyIdentification_Id: data.store?.store_business_id || "",
              currencyCode: data.store?.currencyCode || "",
            };

            self.storeData = {
              ...data.store,
              PartyName: sellerData.PartyName || data.store?.store_name || "",
              PartyIdentification_Id:
                sellerData.PartyIdentification_Id || data.store?.store_business_id || "",
              store_owner: data.store?.store_owner || self.credentials.store || "",
            };

            // Verificar imagen personalizada después de cargar datos de la tienda
            self.checkCustomBackground();

            self.saveStoreData(self.storeData);

            // Configurar perfil y store
            self.profile.name = data.profile.user_name;
            self.profile.email = data.profile.user_email;
            self.profile.token = data.profile.token;
            self.profile.picture = data.profile.user_photo || "";
            self.profile.socialName = "SW67";
            self.profile.user_access = data.profile.user_access;

            self.$store.dispatch("setProfile", self.profile);
            self.$store.dispatch("setSeller", sellerData);
            self.$store.dispatch(
              "setBillingMode",
              (data.store && data.store.billing_mode) || "register"
            );

            // Variables globales
            window.token = data.token;
            window.profile = data.profile;
            window.curvas = data.curvas || [];
            window.store = data.store;
            window.seller = sellerData;

            window.settings = data.settings.reduce((r, a) => {
              r[a.tabla] = r[a.tabla] || [];
              r[a.tabla].push(a);
              return r;
            }, Object.create(null));

            self.$store.dispatch("setNavStatus", true);
            self.$store.dispatch("set_menu", true);

            self.loadingMessage = "Redirigiendo...";
            self.showNotification("¡Bienvenido de vuelta!", "success");

            setTimeout(() => {
              self.isLoggingIn = false;
              self.showLoadingOverlay = false;

              if (self.nextUrl && self.nextUrl !== "/") {
                self.$router.push(self.nextUrl);
              } else {
                const userAccess = data.profile.user_access;
                const roleRoutes = {
                  Analista: "/AnalystDashboard",
                  StoreAdmin: "/Company",
                  VENDEDOR: "/Vendedor",
                };
                if (roleRoutes[userAccess]) {
                  self.$router.push(roleRoutes[userAccess]);
                } else {
                  const billingMode =
                    (data.store && data.store.billing_mode) || "register";
                  const routeByMode = {
                    register: "/",
                    formal: "/InvoiceE",
                    pos: "/InvoiceQ",
                    restaurant: "/InvoicePOS",
                  };
                  self.$router.push(routeByMode[billingMode] || "/");
                }
              }
            }, 300);
          } catch (error) {
            console.error("Error en proceso de login:", error);
            self.handleLoginError("Error en el proceso de inicio de sesión");
          }
        });
      } catch (error) {
        console.error("Error general:", error);
        this.handleLoginError("Error general en el proceso", false);
      }
    },

    validateLoginResponse(data) {
      // Validaciones existentes del código original
      if (!data || typeof data !== "object") {
        return { error: true, message: "Respuesta del servidor inválida" };
      }

      // Continuar con las validaciones existentes...
      return { error: false, message: "Validación exitosa" };
    },
  },
};
</script>

<style scoped>
.login-layout {
  /* Layout principal: dos columnas. Evitamos width:100vw para no forzar scroll horizontal
     en presencia de barras verticales y prevenir "franja" blanca por overflow. */
  display: flex;
  min-height: 100vh;
  width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background: #f5f6f7;
}

/* Sección izquierda - Información de marca */
.login-brand-section {
  /* Columna izquierda ~55% (aspecto visual) usando flex-grow/basis para mejor control */
  flex: 1 1 55%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  position: relative;
  overflow: hidden;
  transition: background-image 0.5s ease-in-out;
  min-height: 100vh;
  background-color: #2c3e50;
  background-image: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
}

/* Encabezado superior de la empresa */
.company-header {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 100%);
  backdrop-filter: blur(10px);
  padding: 20px 10px;
  z-index: 2;
}

.company-header-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.company-logo {
  height: 50px;
  width: auto;
  /* filter: brightness(0) invert(1); */
}

.company-info {
  flex: 1;
}

.company-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

.company-type {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 4px 0 12px 0;
  font-weight: 400;
}

.status-chip {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

/* Área central */
.center-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  /* Sin z-index ni contenido para que la imagen se vea completamente */
  /* Ajuste dinámico: si la pantalla es muy baja, reducir padding para ganar espacio visual */
  padding: clamp(16px, 4vh, 40px);
}

/* Pie de página con contacto */
.contact-footer {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 100%);
  backdrop-filter: blur(10px);
  padding: 30px 40px;
  z-index: 2;
}

.contact-footer-content {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.9rem;
  font-weight: 400;
}

.contact-item v-icon {
  opacity: 0.9;
}

/* Sección derecha - Formulario */
.login-form-section {
  /* Columna derecha ~45% utilizando flex (grow/shrink) */
  flex: 1 1 45%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(24px, 4vh, 48px) clamp(16px, 3vw, 56px);
  /* Eliminar min/max rígidos que provocaban corte en resoluciones intermedias */
  min-width: 320px; /* límite práctico mínimo */
  max-width: 680px; /* que no crezca demasiado en pantallas ultrawide */
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.04);
  position: relative;
  z-index: 2;
}

.form-container {
  width: 100%;
  max-width: 420px; /* permitir que los inputs respiren para no quedar "cortados" */
  display: flex;
  flex-direction: column;
}

.form-header {
  text-align: center;
  margin-bottom: 24px;
  animation: slideInFromBottom 0.5s ease-out both;
}

.form-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #424242;
  margin-bottom: 6px;
  line-height: 1.2;
}

.form-description {
  font-size: 1rem;
  color: #757575;
  line-height: 1.4;
  margin: 0;
}

.not-you-link {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  min-width: auto;
  height: auto;
  padding: 2px 0;
  font-size: 0.8125rem;
}

/* Formulario */
.login-form {
  margin-bottom: 20px;
}

.input-section {
  margin-bottom: 16px;
  animation: slideInFromBottom 0.5s ease-out both;
}

.input-section:nth-child(1) {
  animation-delay: 0.1s;
}

.input-section:nth-child(2) {
  animation-delay: 0.2s;
}

.input-section:nth-child(3) {
  animation-delay: 0.3s;
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #424242;
  margin-bottom: 8px;
  line-height: 1.3;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.forgot-link {
  font-size: 0.8125rem;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  min-width: auto;
  height: auto;
  padding: 4px 0;
}

/* Inputs personalizados */
.custom-input >>> .v-input__control > .v-input__slot {
  border-radius: 6px;
  border-color: #e0e0e0;
  transition: all 0.2s ease;
  min-height: 40px;
  background: #fafafa;
}

.custom-input >>> .v-input__control > .v-input__slot:hover {
  border-color: #bdbdbd;
  background: #ffffff;
}

.custom-input.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #424242;
  border-width: 2px;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(66, 66, 66, 0.1);
}

.custom-input >>> .v-text-field__details {
  padding-top: 6px;
}

/* Opciones del formulario */
.form-options {
  margin-bottom: 20px;
  animation: slideInFromBottom 0.5s ease-out 0.35s both;
}

.remember-checkbox >>> .v-input--selection-controls__input {
  margin-right: 8px;
}

.remember-label {
  font-size: 0.875rem;
  color: #616161;
  font-weight: 500;
}

/* Botón de login */
.login-button {
  border-radius: 6px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.025em;
  height: 40px;
  font-size: 0.875rem;
  animation: slideInFromBottom 0.5s ease-out 0.4s both;
  background: linear-gradient(135deg, #536976 0%, #292e49 100%) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(83, 105, 118, 0.3);
  transition: all 0.2s ease;
}

.login-button:hover {
  background: linear-gradient(135deg, #5f7a88 0%, #333856 100%) !important;
  box-shadow: 0 6px 20px rgba(83, 105, 118, 0.4);
  transform: translateY(-1px);
}

.login-button:disabled {
  opacity: 0.6;
  background: #cccccc !important;
}

/* Footer del formulario */
.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
  font-size: 0.8125rem;
  color: #9e9e9e;
}

.security-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.version-info {
  font-weight: 400;
}

/* Notificaciones */
.notification-content {
  display: flex;
  align-items: center;
}

/* Overlay de carga */
.loading-content {
  text-align: center;
  color: #424242;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 32px 48px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.loading-text {
  margin-top: 16px;
  font-size: 1rem;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .login-layout {
    flex-direction: column;
  }

  .login-brand-section {
    flex: none;
    min-height: clamp(32vh, 44vh, 52vh);
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    padding: 0;
    background-size: cover;
    background-position: center;
  }

  .company-header,
  .contact-footer {
    padding: 20px 30px;
  }

  .company-name {
    font-size: 1.5rem;
  }

  .brand-title {
    font-size: 2.25rem;
    color: #ffffff;
  }

  .features-list {
    display: none;
  }

  .login-form-section {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
    box-shadow: none;
    padding: clamp(24px, 5vh, 56px) clamp(16px, 6vw, 72px);
  }
}

@media (max-width: 768px) {
  .login-brand-section {
    min-height: clamp(26vh, 36vh, 44vh);
    padding: 0;
    background-size: cover;
    background-position: center;
  }

  .company-header,
  .contact-footer {
    padding: 15px 10px;
  }

  .contact-footer-content {
    flex-direction: column;
    gap: 12px;
  }

  .company-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .company-logo {
    height: 40px;
  }

  .company-name {
    font-size: 1.25rem;
  }

  .company-type {
    font-size: 0.875rem;
  }

  .brand-header {
    margin-bottom: 24px;
  }

  .main-logo {
    height: 56px;
  }

  .brand-title {
    font-size: 1.75rem;
  }

  .brand-subtitle {
    font-size: 1rem;
  }

  .login-form-section {
    padding: clamp(20px, 5vh, 40px) clamp(12px, 4vw, 40px);
  }

  .form-title {
    font-size: 1.75rem;
  }

  .form-description {
    font-size: 0.9375rem;
  }

  .store-preview {
    margin-bottom: 24px;
  }

  .store-card {
    padding: 20px;
  }

  .form-footer {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .login-brand-section {
    min-height: clamp(22vh, 30vh, 34vh);
    padding: 16px 10px;
  }

  .main-logo {
    height: 48px;
  }

  .brand-title {
    font-size: 1.5rem;
  }

  .login-form-section {
    padding: clamp(16px, 4vh, 28px) clamp(10px, 4vw, 28px);
  }

  /* Ajustes de robustez adicionales */
  .login-form-section .login-form {
    width: 100%;
  }

  /* Evitar que imágenes o contenido provoquen scroll lateral oculto */
  .login-layout img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Asegurar que el body no introduzca márgenes que generen franja */
  :global(body) {
    margin: 0;
    padding: 0;
    background: #f5f6f7;
  }
  .form-container {
    max-width: none;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .input-section {
    margin-bottom: 20px;
  }

  .custom-input >>> .v-input__control > .v-input__slot {
    min-height: 44px;
  }

  .login-button {
    height: 44px;
    font-size: 0.9375rem;
  }

  .field-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

/* Animaciones */
.v-fade-transition-enter-active,
.v-fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.v-fade-transition-enter,
.v-fade-transition-leave-to {
  opacity: 0;
}

/* Tema oscuro (opcional) */
.theme--dark .login-layout {
  background: linear-gradient(135deg, #121212 0%, #1e1e1e 100%);
}

.theme--dark .login-brand-section {
  background: linear-gradient(135deg, #1e1e1e 0%, #2c2c2c 100%);
  border-right-color: #333333;
}

.theme--dark .login-form-section {
  background: #121212;
}

.theme--dark .brand-title,
.theme--dark .form-title {
  color: #ffffff;
}

.theme--dark .brand-subtitle,
.theme--dark .form-description {
  color: #b0b0b0;
}

.theme--dark .field-label {
  color: #ffffff;
}

.theme--dark .custom-input >>> .v-input__control > .v-input__slot {
  background: #2c2c2c;
  border-color: #404040;
}

.theme--dark .custom-input >>> .v-input__control > .v-input__slot:hover {
  background: #333333;
  border-color: #505050;
}

.theme--dark .store-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .feature-item {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #b0b0b0;
}

.theme--dark .form-footer {
  border-top-color: #333333;
  color: #757575;
}

.theme--dark .register-section {
  border-top-color: #333333;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  33% {
    transform: translateY(-30px) rotate(120deg);
  }
  66% {
    transform: translateY(30px) rotate(240deg);
  }
}

@keyframes slideInFromBottom {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
